import React, { useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { useI18n } from '@/i18n';
import appColors from '@/theme/colors';

const LanguageSwitcher: React.FC = () => {
  const { locale, setLocale, t, availableLocales } = useI18n();
  const localeVisuals = useMemo(
    () =>
      ({
        en: { emoji: '🇬🇧', code: 'EN' },
        sv: { emoji: '🇸🇪', code: 'SV' },
        da: { emoji: '🇩🇰', code: 'DA' },
      }) as const,
    []
  );
  const activeVisual = localeVisuals[locale as keyof typeof localeVisuals] ?? localeVisuals.en;
  const localeLabel = useMemo(() => {
    if (!locale) return 'EN';
    return locale.toUpperCase();
  }, [locale]);

  const handleRotate = () => {
    if (availableLocales.length === 0) return;
    const currentIndex = availableLocales.findIndex((code) => code === locale);
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % availableLocales.length;
    const nextLocale = availableLocales[nextIndex];
    if (nextLocale && nextLocale !== locale) {
      setLocale(nextLocale as typeof locale);
    }
  };

  return (
    <>
      <Pressable
        style={styles.trigger}
        onPress={handleRotate}
        accessibilityRole="button"
        accessibilityLabel={t('language.label')}
      >
        <View style={styles.flag}>
          <Text style={styles.flagEmoji}>{activeVisual.emoji}</Text>
          <Text style={styles.flagCode}>{activeVisual.code ?? localeLabel}</Text>
        </View>
      </Pressable>
    </>
  );
};

const styles = StyleSheet.create({
  trigger: {
    minWidth: 48,
    paddingHorizontal: 6,
    paddingVertical: 6,
    borderRadius: 14,
    backgroundColor: 'transparent',
    marginRight: 8,
    borderWidth: 0,
    alignItems: 'center',
  },
  flag: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: 'transparent',
    borderRadius: 10,
    borderWidth: 0,
    position: 'relative',
    minWidth: 36,
    justifyContent: 'center',
  },
  flagEmoji: {
    fontSize: 16,
    marginRight: 6,
  },
  flagCode: {
    fontSize: 12,
    fontWeight: '800',
    color: appColors.text.secondary,
    letterSpacing: 0.6,
  },
});

export default LanguageSwitcher;
